import { Link, useParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import PageLayout from "@/components/layout/PageLayout";
import { getEventBySlug, events } from "@/mocks/events";
import Timeline from "@/pages/event/detail/components/Timeline";
import KeyPeople from "@/pages/event/detail/components/KeyPeople";
import PerspectivesBlock from "@/pages/event/detail/components/PerspectivesBlock";
import NetizensBlock from "@/pages/event/detail/components/NetizensBlock";
import SourceCheckBlock from "@/pages/event/detail/components/SourceCheckBlock";
import RelatedEvents from "@/pages/event/detail/components/RelatedEvents";

const sections = [
  { id: "sec-timeline", label: "事件时间线", icon: "ri-time-line" },
  { id: "sec-people", label: "关键人物与角色", icon: "ri-group-line" },
  { id: "sec-perspectives", label: "各方观点光谱", icon: "ri-scales-3-line" },
  { id: "sec-netizens", label: "网友评论观察", icon: "ri-message-3-line" },
  { id: "sec-source", label: "信息溯源清单", icon: "ri-search-eye-line" },
];

function statusPill(status: string) {
  if (status === "持续发酵") return "bg-primary-500 text-background-50";
  if (status === "热度回落") return "bg-secondary-200 text-foreground-700";
  return "bg-accent-100 text-accent-900";
}

export default function EventDetail() {
  const { slug } = useParams<{ slug: string }>();
  const event = getEventBySlug(slug || "");

  if (!event) {
    return (
      <PageLayout>
        <Seo
          title="未找到该事件地图 - 吃瓜路线"
          description="你访问的事件地图不存在或已被调整，返回事件地图总览继续浏览。"
          path={`/events/${slug || ""}`}
        />
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-28 text-center">
          <i className="ri-compass-discover-line text-6xl text-foreground-300" aria-hidden="true"></i>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-900">
            这份事件地图走丢了
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            它可能被合并到其他话题，或还没被整理出来。
          </p>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-road-map-line" aria-hidden="true"></i>
            返回事件地图总览
          </Link>
        </div>
      </PageLayout>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "事件地图", item: SITE_URL + "/events" },
          { "@type": "ListItem", position: 3, name: event.title, item: SITE_URL + "/events/" + event.slug },
        ],
      },
      {
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        image: event.cover,
        dateModified: event.updated,
        author: { "@type": "Organization", name: "吃瓜路线" },
        publisher: { "@type": "Organization", name: "吃瓜路线" },
        mainEntityOfPage: SITE_URL + "/events/" + event.slug,
      },
    ],
  };

  return (
    <PageLayout>
      <Seo
        title={`${event.title} - 吃瓜路线`}
        description={event.summary.slice(0, 150)}
        keywords={`${event.title},${event.tags.join(",")},事件时间线,观点谱,信息溯源`}
        path={`/events/${event.slug}`}
        type="article"
        image={event.cover}
        schema={schema}
      />

      {/* 面包屑 */}
      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li>
            <Link to="/events" className="hover:text-primary-700">
              事件地图
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li aria-current="page" className="max-w-[16rem] truncate font-medium text-foreground-700">
            {event.title}
          </li>
        </ol>
      </nav>

      {/* 头部 */}
      <header className="mx-auto w-full max-w-7xl px-4 pt-10 md:px-6 md:pt-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_420px] lg:items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <Link
                to={`/events?cat=${event.categorySlug}`}
                className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-200"
              >
                {event.category}
              </Link>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusPill(event.status)}`}>
                {event.status}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-accent-700">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                热度 {event.heat}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-700">
              {event.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
                更新于 {event.updated}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm" aria-hidden="true"></i>
                约 {event.readingMinutes} 分钟读完
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-file-list-3-line text-sm" aria-hidden="true"></i>
                {event.timeline.length} 个时间节点 · {event.perspectives.length} 方观点
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {event.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                >
                  #{t}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2">
              <img
                src={event.cover}
                alt={event.coverAlt}
                className="aspect-[4/3] w-full rounded-md object-cover object-top"
              />
            </div>
            <span className="absolute -bottom-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50">
              <i className="ri-map-pin-2-line" aria-hidden="true"></i>
              事件地图 · 由吃瓜路线整理
            </span>
          </div>
        </div>
      </header>

      {/* 主体 */}
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-12 md:px-6 lg:grid-cols-4 lg:py-16">
        {/* 目录栏 */}
        <aside className="order-2 lg:order-1 lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 lg:sticky lg:top-20">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
              <i className="ri-list-unordered text-accent-600" aria-hidden="true"></i>
              本页导读
            </h2>
            <ul className="mt-3 space-y-1">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
                  >
                    <i className={`${s.icon} text-accent-700`} aria-hidden="true"></i>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-5 border-t border-background-200 pt-4 text-xs text-foreground-500">
              <p className="flex items-center justify-between">
                <span>事件状态</span>
                <span className="font-medium text-foreground-800">{event.status}</span>
              </p>
              <p className="mt-2 flex items-center justify-between">
                <span>时间节点</span>
                <span className="font-medium text-foreground-800">{event.timeline.length} 个</span>
              </p>
              <p className="mt-2 flex items-center justify-between">
                <span>观点方</span>
                <span className="font-medium text-foreground-800">{event.perspectives.length} 方</span>
              </p>
              <p className="mt-2 flex items-center justify-between">
                <span>参考热度</span>
                <span className="font-medium text-accent-700">{event.heat}</span>
              </p>
            </div>
          </div>
        </aside>

        {/* 正文 */}
        <article className="order-1 space-y-14 lg:order-2 lg:col-span-3">
          <Timeline items={event.timeline} />
          <KeyPeople people={event.people} />
          <PerspectivesBlock perspectives={event.perspectives} />
          <NetizensBlock netizens={event.netizens} />
          <SourceCheckBlock check={event.sourceCheck} />
          <div className="border-t border-background-200 pt-12">
            <RelatedEvents current={event.slug} />
          </div>
        </article>
      </div>
    </PageLayout>
  );
}